import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const RANGE_OPTIONS = [
  { value: "ALL", label: "All time" },
  { value: "L30D", label: "Last 30 days" },
  { value: "MTD", label: "Month to date" },
  { value: "QTD", label: "Quarter to date" },
  { value: "YTD", label: "Year to date" },
  { value: "CUSTOM", label: "Custom range" },
];

const ALL = "__all__";

function MasterSelect({ def, value, onChange }) {
  const { data: opts = [] } = useQuery({
    queryKey: ["master", def.list_type, "reports"],
    queryFn: async () => (await api.get(`/master-data/${def.list_type}`)).data,
  });
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="mt-1" data-testid={`filter-${def.key}`}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {opts.map((o) => (
          <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function UserSelect({ def, value, onChange, locked, lockedLabel }) {
  const { data: users = [] } = useQuery({
    queryKey: ["report-users"],
    queryFn: async () => (await api.get("/auth/users")).data,
    enabled: !locked,
  });
  if (locked) {
    return (
      <Input value={lockedLabel} disabled className="mt-1 bg-slate-50 text-slate-500" data-testid={`filter-${def.key}-locked`} />
    );
  }
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="mt-1" data-testid={`filter-${def.key}`}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function StaticSelect({ def, value, onChange }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="mt-1" data-testid={`filter-${def.key}`}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {def.options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function LeadGroupSelect({ def, value, onChange }) {
  const { data: groups = [] } = useQuery({
    queryKey: ["lead-groups", "reports"],
    queryFn: async () => (await api.get("/lead-groups")).data,
  });
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="mt-1" data-testid={`filter-${def.key}`}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {groups.map((g) => (
          <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BrokerSelect({ def, value, onChange }) {
  const { data: brokers = [] } = useQuery({
    queryKey: ["brokers", "reports"],
    queryFn: async () => (await api.get("/brokers")).data,
  });
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="mt-1" data-testid={`filter-${def.key}`}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {brokers.map((b) => (
          <SelectItem key={b.id} value={b.id}>{b.broker_name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AssetSelect({ def, value, onChange }) {
  const { data: assets = [] } = useQuery({
    queryKey: ["assets", "reports"],
    queryFn: async () => (await api.get("/assets")).data,
  });
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="mt-1" data-testid={`filter-${def.key}`}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {assets.map((a) => (
          <SelectItem key={a.id} value={a.id}>{a.name}{a.asset_code ? ` (${a.asset_code})` : ""}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function CommonFilters({ filters = [], values, setValue }) {
  const { user } = useAuth();
  const roles = user?.roles || [user?.role];
  const isAdmin = roles.includes("admin");

  return (
    <div className="space-y-4" data-testid="report-filters">
      {filters.map((def) => {
        if (def.control === "date_range") {
          return (
            <div key={def.key}>
              <Label className="text-xs text-slate-600">{def.label}</Label>
              <Select value={values.range_key || "ALL"} onValueChange={(v) => setValue("range_key", v)}>
                <SelectTrigger className="mt-1" data-testid="filter-date-range"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RANGE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {values.range_key === "CUSTOM" && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input type="date" value={values.custom_from || ""} onChange={(e) => setValue("custom_from", e.target.value)} className="text-xs" data-testid="filter-custom-from" />
                  <Input type="date" value={values.custom_to || ""} onChange={(e) => setValue("custom_to", e.target.value)} className="text-xs" data-testid="filter-custom-to" />
                </div>
              )}
            </div>
          );
        }
        const param = def.param;
        const locked = !isAdmin && (def.key === "lead_owner" || def.key === "logged_by");
        return (
          <div key={def.key}>
            <Label className="text-xs text-slate-600">{def.label}</Label>
            {def.control === "master" && (
              <MasterSelect def={def} value={values[param]} onChange={(v) => setValue(param, v)} />
            )}
            {def.control === "user" && (
              <UserSelect def={def} value={values[param]} onChange={(v) => setValue(param, v)} locked={locked} lockedLabel={user?.name || "You"} />
            )}
            {def.control === "select" && (
              <StaticSelect def={def} value={values[param]} onChange={(v) => setValue(param, v)} />
            )}
            {def.control === "lead_group" && (
              <LeadGroupSelect def={def} value={values[param]} onChange={(v) => setValue(param, v)} />
            )}
            {def.control === "broker" && (
              <BrokerSelect def={def} value={values[param]} onChange={(v) => setValue(param, v)} />
            )}
            {def.control === "asset" && (
              <AssetSelect def={def} value={values[param]} onChange={(v) => setValue(param, v)} />
            )}
          </div>
        );
      })}
    </div>
  );
}
